"use client";

import { Moon, Sun, SunMoon } from "lucide-react";
import { useTheme } from "next-themes";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";

const THEME_CYCLE = ["light", "dark", "system"] as const;
type ThemeSetting = (typeof THEME_CYCLE)[number];

const themeLabel: Record<ThemeSetting, string> = {
  light: "Light",
  dark: "Dark",
  system: "System",
};

const ThemeIcon = ({ theme }: { theme: ThemeSetting }) => {
  const className = "h-[1.2rem] w-[1.2rem]";
  if (theme === "dark") return <Moon className={className} aria-hidden />;
  if (theme === "system") return <SunMoon className={className} aria-hidden />;
  return <Sun className={className} aria-hidden />;
};

export const ThemeToggle = () => {
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const activeTheme: ThemeSetting = theme === "light" || theme === "dark" || theme === "system" ? theme : "system";

  const cycleTheme = () => {
    const index = THEME_CYCLE.indexOf(activeTheme);
    const next = THEME_CYCLE[(index + 1) % THEME_CYCLE.length];
    setTheme(next);
  };

  if (!mounted) {
    return (
      <Button variant="outline" size="icon" disabled aria-label="Switch theme">
        <Sun className="h-[1.2rem] w-[1.2rem]" aria-hidden />
      </Button>
    );
  }

  return (
    <Button
      type="button"
      variant="outline"
      size="icon"
      onClick={cycleTheme}
      aria-label={`Switch theme (currently ${themeLabel[activeTheme]})`}
    >
      <ThemeIcon theme={activeTheme} />
    </Button>
  );
};
